import CategoryTag from "@components/CategoryTag/CategoryTag";
import DescriptionTag from "@components/DescriptionTag/DescriptionTag";
import { ThemeEnum } from "@components/ThemeProvider/ThemeProvider";
import VideoPlayButton from "@src/app/assets/icons/video-playbutton.svg?react";
import PlaceholderImage from "@src/app/assets/images/placeholder.svg";
import type { BFFArticleCard } from "@src/app/types/bff";
import clsx from "clsx";

import Link from "../Link";

type ArticleCardProps = {
  article: BFFArticleCard;
  imageAlt?: string;
  className?: string;
  theme?: ThemeEnum;
  variant?: "Gary";
  titleVariant?: "header-2" | "header-4";
};

export default function ArticleCard({
  article,
  imageAlt = "",
  theme,
  variant,
  className,
  titleVariant = "header-4",
}: ArticleCardProps) {
  const {
    cover,
    sectionName,
    title,
    description,
    teaser,
    authors,
    displayType,
    section,
  } = article;

  const isVideo = displayType == "video";
  const isDark = theme == ThemeEnum.dark;
  const isGary = variant === "Gary";
  const descriptionTag =
    displayType === "big_read"
      ? "BigRead"
      : displayType === "photo_story"
        ? "PhotoStory"
        : undefined;

  return (
    <div
      data-testid={`article-card-${article.id}`}
      className={clsx(
        "flex h-full w-full flex-col items-start gap-3 px-3 py-4 lg:max-w-150",
        isDark && "bg-background-primary-on-dark",
        className,
      )}
    >
      <div className="group h-full w-full">
        <div className={clsx("flex h-full flex-col", !isGary && "gap-5!")}>
          {/* Image Section */}
          <div className="relative aspect-3/2 w-full overflow-hidden">
            <Link to={article.urlPath}>
              <img
                alt={imageAlt}
                className="h-full w-full object-cover transition-transform duration-300 lg:group-hover:scale-105"
                src={cover || PlaceholderImage}
              />
              {isVideo ? (
                <div className="absolute inset-0 flex items-center justify-center">
                  <VideoPlayButton />
                </div>
              ) : null}

              {descriptionTag ? (
                <div className="absolute top-0 right-0">
                  <DescriptionTag name={descriptionTag} />
                </div>
              ) : null}
            </Link>
          </div>

          {/* Content Section */}
          <div
            className={clsx(
              "flex w-full flex-1 flex-col items-start gap-2",
              isGary && "bg-background-secondary px-5 py-4",
              isDark && "bg-background-primary-on-dark!",
            )}
          >
            <CategoryTag
              name={sectionName || "Politics"}
              href={`/${section}`}
            />
            <Link to={article.urlPath} className="flex flex-col gap-2">
              <p
                className={clsx(
                  titleVariant === "header-2"
                    ? "lg:text-header-2 text-header-4"
                    : "text-header-4",
                  "group-hover:text-link-hover break-word w-full wrap-anywhere transition-transform duration-300 lg:line-clamp-5",
                  isDark
                    ? "text-primary-on-dark group-hover:text-link-video-hover"
                    : "text-primary hover:text-primary-hover",
                )}
              >
                {title}
              </p>
              {(description || teaser) && (
                <p
                  className={clsx(
                    "text-body-2 break-word line-clamp-3 w-full wrap-anywhere",
                    isDark ? "text-secondary-on-dark" : "text-secondary",
                  )}
                >
                  {description || teaser}
                </p>
              )}
              {authors[0]?.name && (
                <p
                  className={clsx(
                    "text-label-2 font-bold break-all",
                    isDark ? "text-tertiary-on-dark" : "text-tertiary",
                  )}
                >
                  {authors[0].name}
                </p>
              )}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
